import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Sparkles,
  CalendarCheck,
  CreditCard,
  ShieldCheck,
  ArrowRight,
  Users,
  Building2,
  TrendingUp,
} from "lucide-react";
import { LoginForm } from "./login-form";
import { LoginRedirect } from "./login-redirect";
import { ModeToggle } from "@/components/ui/mode-toggle";
import { UserRegistrationFlow } from "@/components/registration/user-registration-flow";
import { useAuth } from "@/hooks/use-auth";
import { InnerShadowTopIcon } from "@/utilities/icon-utils";
import { cn } from "@/utilities/tailwind-utils";
import { LoadingCard } from "@/components/ui/loading-card";
import { captureReferralCodeFromSearch } from "@/utilities/referral-utils";

// Rotating taglines for the hero
const TAGLINES = [
  "Intelligent Matching",
  "Secure Scheduling",
  "Seamless Payments",
  "Quality Assurance"
];

// Feature data with Lucide icons
const FEATURES = [
  {
    title: "AI-Powered Matching",
    description:
      "Advanced AI models rigorously match & align practices with optimal professionals smoothly.",
    icon: Sparkles
  },
  {
    title: "Secure Scheduling",
    description:
      "Advanced scheduling system with conflict detection and real-time availability updates.",
    icon: CalendarCheck
  },
  {
    title: "Payment Processing",
    description:
      "Secure payment handling with automated invoicing and transparent fee structures.",
    icon: CreditCard
  },
  {
    title: "Quality Assurance",
    description:
      "Comprehensive rating and review system to maintain high standards of service delivery.",
    icon: ShieldCheck
  }
];

// Stats for social proof
const STATS = [
  { label: "Professionals", value: "2,500+", icon: Users },
  { label: "Practices", value: "850+", icon: Building2 },
  { label: "Shifts Filled", value: "15K+", icon: TrendingUp }
];

// Rotating tagline hook
function useRotatingTagline() {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % TAGLINES.length);
        setVisible(true);
      }, 400);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return { tagline: TAGLINES[index], visible };
}

// Active feature cycling hook
function useActiveFeature() {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % FEATURES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return { activeIndex, setActiveIndex };
}

/** Shared animated "Welcome to Tempii" heading */
function WelcomeToTempiiHeading({
  compact,
  className
}: {
  compact?: boolean;
  className?: string;
}) {
  return (
    <h1
      className={cn(
        "login-fade-in-up login-delay-200 text-center",
        compact ? "mb-0" : "mb-2",
        className
      )}
    >
      <span
        className={cn(
          "block font-bold tracking-tight",
          "text-slate-800 dark:text-foreground",
          compact
            ? "text-2xl sm:text-3xl"
            : "text-3xl md:text-4xl xl:text-5xl"
        )}
      >
        Welcome to
      </span>
      <span
        className={cn(
          "block font-extrabold tracking-tighter mt-1",
          "text-blue-600 dark:text-blue-400",
          compact ? "text-4xl sm:text-5xl" : "text-5xl md:text-6xl xl:text-7xl"
        )}
      >
        Tempii
      </span>
    </h1>
  );
}

// Floating background orb
function FloatingOrb({
  className,
  size,
  delay
}: {
  className?: string;
  size: string;
  delay?: string;
}) {
  return (
    <div
      className={cn(
        "absolute rounded-full opacity-[0.15] dark:opacity-[0.04] blur-3xl pointer-events-none",
        size,
        className
      )}
      style={{ animationDelay: delay }}
    />
  );
}

// Interactive feature card with active state
function FeatureCard({
  feature,
  index,
  isActive,
  onHover
}: {
  feature: (typeof FEATURES)[number];
  index: number;
  isActive: boolean;
  onHover: () => void;
}) {
  const Icon = feature.icon;
  const delayClass = `login-delay-${(index + 3) * 100}`;

  return (
    <div
      onMouseEnter={onHover}
      className={cn(
        "login-fade-in-up group relative overflow-hidden rounded-xl",
        "border transition-all duration-500 ease-out cursor-default",
        "backdrop-blur-sm",
        isActive
          ? cn(
            "border-blue-200/80 dark:border-white/[0.12]",
            "bg-white/80 dark:bg-white/[0.06]",
            "shadow-sm shadow-slate-200/60 dark:shadow-black/30",
            "scale-[1.01]"
          )
          : cn(
            "border-slate-200/60 dark:border-white/[0.04]",
            "bg-white/50 dark:bg-white/[0.02]",
            "hover:border-blue-200/60 dark:hover:border-white/[0.08]",
            "hover:bg-white/70 dark:hover:bg-white/[0.04]",
            "hover:shadow-sm hover:shadow-slate-200/50 dark:hover:shadow-black/20"
          ),
        delayClass
      )}
    >
      {/* Active indicator bar */}
      <div
        className={cn(
          "absolute left-0 top-0 bottom-0 w-[3px] rounded-full transition-all duration-500",
          "bg-blue-600 dark:bg-blue-400",
          isActive ? "opacity-100" : "opacity-0"
        )}
      />

      {/* Glow overlay */}
      <div
        className={cn(
          "absolute inset-0 transition-opacity duration-500",
          "bg-gradient-to-br from-blue-500/[0.06] to-transparent dark:from-blue-400/[0.06]",
          isActive ? "opacity-100" : "opacity-0 group-hover:opacity-60"
        )}
      />

      <div className="relative flex items-start gap-3 px-4 py-3.5">
        <div
          className={cn(
            "flex shrink-0 items-center justify-center rounded-lg p-2",
            "bg-slate-100 text-blue-600 dark:bg-white/[0.06] dark:text-blue-400",
            "transition-all duration-500",
            isActive ? "scale-110" : "group-hover:scale-105"
          )}
        >
          <Icon className="size-4" strokeWidth={1.8} />
        </div>
        <div className="min-w-0 flex-1">
          <h3
            className={cn(
              "text-sm font-semibold leading-tight transition-colors duration-300",
              isActive
                ? "text-slate-900 dark:text-foreground"
                : "text-slate-700 dark:text-foreground/80"
            )}
          >
            {feature.title}
          </h3>
          <p
            className={cn(
              "text-xs leading-snug mt-1 transition-all duration-500",
              isActive
                ? "text-slate-500 dark:text-muted-foreground max-h-20 opacity-100"
                : "text-slate-400 dark:text-muted-foreground/60 max-h-0 sm:max-h-20 opacity-0 sm:opacity-70"
            )}
          >
            {feature.description}
          </p>
        </div>
        <ArrowRight
          className={cn(
            "size-4 mt-1 shrink-0 transition-all duration-300",
            isActive
              ? "text-blue-500 dark:text-foreground/40 translate-x-0 opacity-100"
              : "text-foreground/0 -translate-x-2 opacity-0"
          )}
        />
      </div>
    </div>
  );
}

// Stat counter pill
function StatPill({
  stat,
  index
}: {
  stat: (typeof STATS)[number];
  index: number;
}) {
  const Icon = stat.icon;
  const delayClass = `login-delay-${(index + 5) * 100}`;

  return (
    <div
      className={cn(
        "login-fade-in-up flex items-center gap-2 px-3.5 py-2 rounded-lg",
        "border border-slate-200/80 dark:border-white/[0.06]",
        "bg-white/60 dark:bg-white/[0.03]",
        "backdrop-blur-sm",
        "shadow-sm shadow-slate-200/50 dark:shadow-none",
        delayClass
      )}
    >
      <Icon className="size-3.5 text-blue-600/70 dark:text-muted-foreground/70" />
      <span className="text-sm font-bold text-slate-800 dark:text-foreground">
        {stat.value}
      </span>
      <span className="text-xs text-slate-500 dark:text-muted-foreground">
        {stat.label}
      </span>
    </div>
  );
}

// Main Login Page
export default function Login() {
  const { isLoading } = useAuth();
  const { tagline, visible } = useRotatingTagline();
  const { activeIndex, setActiveIndex } = useActiveFeature();

  // Park any `?ref=` code before the Entra redirect tears the SPA down.
  // The registration wizard picks it back up on the way in.
  useEffect(() => {
    captureReferralCodeFromSearch(window.location.search);
  }, []);

  const features = useMemo(() => FEATURES, []);
  const stats = useMemo(() => STATS, []);

  const handleFeatureHover = useCallback(
    (index: number) => () => setActiveIndex(index),
    [setActiveIndex]
  );

  if (isLoading) {
    return (
      <LoadingCard spinnerSize="lg" fullScreen={true} showThemeToggle={true} />
    );
  }

  return (
    <>
      <LoginRedirect />

      <UserRegistrationFlow showRegistrationWizard={true}>
        <div
          className={cn(
            "relative min-h-svh flex flex-col overflow-hidden",
            "bg-slate-50 dark:bg-zinc-950"
          )}
        >
          {/* Single subtle accent orb */}
          <FloatingOrb
            className="login-float bg-blue-400 dark:bg-blue-500 -top-24 -right-24"
            size="w-[30rem] h-[30rem]"
          />

          {/* Grid pattern overlay */}
          <div
            className="absolute inset-0 opacity-[0.03] dark:opacity-[0.03] pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)",
              backgroundSize: "32px 32px"
            }}
          />

          {/* Radial spotlight in hero area */}
          <div
            className="absolute top-1/4 left-1/4 w-[700px] h-[700px] pointer-events-none opacity-[0.07] dark:opacity-[0.05]"
            style={{
              background:
                "radial-gradient(circle, rgba(37,99,235,0.5) 0%, transparent 70%)"
            }}
          />

          {/* Top bar: Brand + Theme toggle */}
          <div className="relative z-10 flex items-center justify-between px-6 md:px-10 pt-6">
            <div className="login-fade-in-up flex items-center gap-2">
              <a
                href="#"
                className="flex items-center gap-2.5 font-semibold text-foreground no-underline"
              >
                <div
                  className={cn(
                    "flex size-8 items-center justify-center rounded-lg",
                    "bg-blue-600 text-white",
                    "shadow-sm"
                  )}
                >
                  <InnerShadowTopIcon className="size-6" />
                </div>
                <span className="text-lg tracking-tight font-semibold text-slate-800 dark:text-foreground">
                  Tempii
                </span>
              </a>
            </div>
            <div className="login-fade-in-up">
              <ModeToggle />
            </div>
          </div>

          {/* Shared panel: two columns on lg (2.5 : 1); stacked on mobile */}
          <div className="relative z-10 flex flex-1 flex-col px-2 sm:px-6 md:px-10 pb-8 pt-6 lg:pt-8 lg:pb-8">
            <div
              className={cn(
                "mx-auto w-full max-w-8xl flex-1",
                "lg:rounded-3xl lg:border",
                "lg:border-slate-200/70 lg:dark:border-white/[0.08]",
                "lg:bg-white/40 lg:dark:bg-white/[0.03]",
                "lg:backdrop-blur-xl",
                "lg:shadow-xl lg:shadow-slate-200/50 lg:dark:shadow-black/40",
                "overflow-hidden",
                "flex flex-col lg:grid lg:min-h-[min(720px,calc(100svh-9rem))]",
                "lg:grid-cols-[minmax(0,2.5fr)_minmax(0,1fr)]"
              )}
            >
              {/* ——— Desktop hero (left, ~71% width) ——— */}
              <div
                className={cn(
                  "hidden lg:flex flex-col",
                  "px-8 xl:px-14 py-8 xl:py-10",
                  "border-b lg:border-b-0 lg:border-r",
                  "border-slate-200/60 dark:border-white/[0.06]",
                  "relative"
                )}
              >
                {/* Subtle hero-internal gradient for depth */}
                <div className="absolute inset-0 bg-gradient-to-br from-transparent via-slate-50/40 to-slate-100/30 dark:from-transparent dark:via-transparent dark:to-transparent pointer-events-none" />

                {/* Top section: Badge + Title + Tagline */}
                <div className="relative flex-1 flex flex-col justify-center">
                  <div className="login-fade-in-up login-delay-100 mb-5 mx-auto">
                    <div
                      className={cn(
                        "inline-flex items-center gap-2 rounded-full px-4 py-1.5",
                        "border border-slate-200/80 dark:border-white/[0.08]",
                        "bg-white/70 dark:bg-white/[0.05]",
                        "backdrop-blur-sm",
                        "shadow-sm shadow-slate-200/40 dark:shadow-none",
                        "text-xs font-medium text-slate-600 dark:text-muted-foreground"
                      )}
                    >
                      <span className="relative flex size-2">
                        <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                        <span className="relative inline-flex size-2 rounded-full bg-emerald-500" />
                      </span>
                      AI-Powered Healthcare Staffing
                    </div>
                  </div>

                  <WelcomeToTempiiHeading />

                  <div className="login-fade-in-up login-delay-300 mt-4 mb-6 text-center">
                    <p className="text-base text-slate-500 dark:text-muted-foreground leading-relaxed max-w-md mx-auto">
                      The dental staffing platform built for{" "}
                      <span
                        className={cn(
                          "login-tagline-text inline-block font-semibold",
                          "text-blue-600 dark:text-blue-400",
                          visible
                            ? "opacity-100 translate-y-0"
                            : "opacity-0 -translate-y-2"
                        )}
                      >
                        {tagline}
                      </span>
                    </p>
                  </div>

                  {/* Stats row */}
                  <div className="login-fade-in-up login-delay-400 flex items-center justify-center gap-3 mb-8">
                    {stats.map((stat, index) => (
                      <StatPill key={stat.label} stat={stat} index={index} />
                    ))}
                  </div>
                </div>

                {/* Feature cards grid - anchored to bottom */}
                <div className="relative grid grid-cols-2 gap-3">
                  {features.map((feature, index) => (
                    <FeatureCard
                      key={feature.title}
                      feature={feature}
                      index={index}
                      isActive={activeIndex === index}
                      onHover={handleFeatureHover(index)}
                    />
                  ))}
                </div>

                {/* Bottom accent dots */}
                <div className="relative login-fade-in login-delay-700 mt-6 flex justify-center">
                  <div className="flex items-center gap-1.5">
                    {FEATURES.map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setActiveIndex(i)}
                        className={cn(
                          "transition-all duration-500 rounded-full",
                          activeIndex === i
                            ? "w-6 h-1.5 bg-blue-600"
                            : "w-1.5 h-1.5 bg-slate-300 dark:bg-muted-foreground/20 hover:bg-slate-400 dark:hover:bg-muted-foreground/40"
                        )}
                        aria-label={`Show feature ${i + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* ——— Login column (right on lg); mobile: title + form ——— */}
              <div
                className={cn(
                  "flex flex-col justify-center",
                  "px-6 sm:px-8 py-8 lg:py-10",
                  "min-w-0",
                  "bg-gradient-to-b from-white/30 to-slate-50/50 dark:from-transparent dark:to-transparent"
                )}
              >
                {/* Mobile-only animated welcome */}
                <div className="lg:hidden mb-8 flex justify-center">
                  <WelcomeToTempiiHeading compact />
                </div>

                <div
                  className={cn(
                    "login-fade-in-up login-delay-400 w-full mx-auto max-w-sm",
                    "lg:rounded-2xl lg:border",
                    "lg:border-slate-200/80 lg:dark:border-white/[0.12]",
                    "lg:bg-white/80 lg:dark:bg-white/[0.06]",
                    "lg:backdrop-blur-2xl",
                    "lg:shadow-xl lg:shadow-slate-200/50 lg:dark:shadow-black/40",
                    "lg:ring-1 lg:ring-slate-200/50 lg:dark:ring-white/[0.06]",
                    "lg:p-8"
                  )}
                >
                  <LoginForm />
                </div>

                <p className="login-fade-in login-delay-600 text-center text-xs text-slate-400 dark:text-muted-foreground/50 mt-6">
                  By continuing, you agree to Tempii's Terms of Service and
                  Privacy Policy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </UserRegistrationFlow>
    </>
  );
}
